import { type EntityState } from '@ngrx/entity';
import { type Action, createAction, props } from '@ngrx/store';

import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementInput,
  type CanvasElementOverrides,
  type CanvasElementOverrideTarget,
  type CanvasElementType,
  type CanvasNamespaceId,
  type ColumnId,
  type ColumnResizeData,
  type ElementTargetPath,
  type FormOutputUpdateData,
  type LayoutData,
  type NewColumnPlacement,
  type PartialPropsOf,
  type PropsOf,
  type SlotAlignment,
  type SlotId,
  type SlotSizing,
  type SlotSizingMode,
} from '@blaze-runtime/engine-domain';
import {
  type ConnectionId,
  type PageInfo,
  type PageType,
} from '@blaze-shared/api-contracts-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementInputBinding,
  type DataBindingSourceInfo,
  type DataSourceSelectMode,
  type ElementMetaData,
  type ElementPropsSection,
  type ElementPropsUpdate,
  type ElementPropUpdate,
  type ItemContainerActionsIds,
  type PropertiesConfigSubsectionTriggerKey,
  type PropertiesEditorSavePropsTarget,
  type RenderElementContext,
  type RenderElementDataRowContext,
} from '@blaze-shared/elements-domain';
import { type NavigationPageType } from '@blaze-shared/projects-domain';
import {
  type BaseSchema,
  type BlazeTableSchema,
  type DatasetColumn,
  type ElementSchema,
  type RuntimeEventType,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { type StyleSheet } from '@blaze-shared/theming-domain';
import { type Connection, type FormActionType, type Workflow } from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { type FieldInfo } from '../../canvas-elements/field-info';
import { type OverlayElement } from '../../elements/_overlay/_overlay.element';
import { type PlaceElementStyleData } from '../../utils/layout/place-element';

const TAG = '[Elements]';

/** Layout was fetched from an external source and is being prepared for use in the app */
export const layoutFetched = createAction(`${TAG} Layout fetched`, props<{ layout: LayoutData }>());

/** Check what elements to generate for the layout */
export const initializeLayout = createAction(
  `${TAG} Initialize layout`,
  props<{ layout: LayoutData }>(),
);

/** Layout was initialized with default elements */
export const layoutInitialized = createAction(
  `${TAG} Layout initialized`,
  props<{ layout: LayoutData; elements?: CanvasElement[] }>(),
);

/** Layout has a prepared element */
export const layoutReady = createAction(
  `${TAG} Layout ready`,
  props<{
    layout: LayoutData;
    elements?: CanvasElement[];
  }>(),
);

export const layoutLoadingError = createAction(
  `${TAG} Layout loading error`,
  props<{ layout: LayoutData; err: any }>(),
);

/** Layout was saved */
export const layoutsSaved = createAction(
  `${TAG} Layouts saved`,
  props<{ layouts: LayoutData[] }>(),
);

export const markLayoutsOffSync = createAction(
  `${TAG} Mark layouts off sync`,
  props<{ layoutsIds: CanvasNamespaceId[] }>(),
);

/** Replace elements for certain namespace */
export const replaceElements = createAction(
  `${TAG} Replace elements`,
  props<{ ns: CanvasNamespaceId; elements: CanvasElement[] }>(),
);

export const selectPath = createAction(
  `${TAG} Select path`,
  props<{
    elementId: CanvasElementId;
    slotId?: SlotId;
    rowContext?: RenderElementDataRowContext;
  }>(),
);

export const selectParent = createAction(
  `${TAG} Select parent`,
  props<{ elementId: CanvasElementId }>(),
);

export const resetSelection = createAction(`${TAG} Reset selection`);

export const addNewElementAtPath = createAction(
  `${TAG} Add new element at path`,
  props<{
    elementDefinition: CanvasElementDefinitionInternal;
    atPath?: ElementTargetPath;
    initProps?: Partial<PropsOf<any>>;
    style?: PlaceElementStyleData;
  }>(),
);

export const generatedNewNavigationElement = createAction(
  `${TAG} Generated new navigation element`,
  props<{ elementDefinition: CanvasElementDefinitionInternal }>(),
);

export const addExistingElementAtPathSafe = createAction(
  `${TAG} Add existing element at path Safe`,
  props<{
    elementId: CanvasElementId;
    atPath?: ElementTargetPath;
    skipSelect?: boolean;
    removeFromCurrentParent?: boolean;
  }>(),
);

export const addExistingElementAtPath = createAction(
  `${TAG} Add existing element at path`,
  props<{
    elementId: CanvasElementId;
    atPath?: ElementTargetPath;
    skipSelect?: boolean;
    removeFromCurrentParent?: boolean;
  }>(),
);

export const addColumn = createAction(
  `${TAG} Add column`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    columnPlacement: NewColumnPlacement;
    sizing: SlotSizing;
  }>(),
);

export const setSlotAlignment = createAction(
  `${TAG} Set slot alignment`,
  props<{
    elementId: CanvasElementId;
    slotId: SlotId;
    alignment: SlotAlignment;
  }>(),
);

export const setSlotHorizontalAlignment = createAction(
  `${TAG} Set slot horizontal alignment`,
  props<{
    elementId: CanvasElementId;
    slotId: SlotId;
    horizontalAlignment: SlotAlignment['horizontal'];
  }>(),
);

export const setSlotVerticalAlignment = createAction(
  `${TAG} Set slot vertical alignment`,
  props<{
    elementId: CanvasElementId;
    slotId: SlotId;
    verticalAlignment: SlotAlignment['vertical'];
  }>(),
);

export const setElementSlotsAlignment = createAction(
  `${TAG} Set element alignment`,
  props<{
    elementId: CanvasElementId;
    alignment: SlotAlignment;
  }>(),
);

export const triggerElementClick = createAction(
  `${TAG} Trigger element click`,
  props<{ elementId: CanvasElementId; elementContext?: RenderElementContext }>(),
);

export const setColumnWidthDrag = createAction(
  `${TAG} Set column width drag`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    width: number;
  }>(),
);

export const setColumnWidthDirect = createAction(
  `${TAG} Set column width direct`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    width: number;
    resizeData: ColumnResizeData[];
  }>(),
);

export const equalizeColumnsWidth = createAction(
  `${TAG} Equalize columns width`,
  props<{ elementId: CanvasElementId }>(),
);

export const setColumnSizingMode = createAction(
  `${TAG} Set column width mode`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    mode: SlotSizingMode;
    resizeData: ColumnResizeData[];
  }>(),
);

export const setElementWidth = createAction(
  `${TAG} Set element width`,
  props<{
    elementId: CanvasElementId;
    width: number;
  }>(),
);

export const setElementHeight = createAction(
  `${TAG} Set element height`,
  props<{
    elementId: CanvasElementId;
    height: number;
    elementMeta?: ElementMetaData;
  }>(),
);

export const deleteElement = createAction(
  `${TAG} Delete element`,
  props<{ elementId: CanvasElementId }>(),
);

export const deleteColumn = createAction(
  `${TAG} Delete column`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
  }>(),
);

export const deleteTry = createAction(
  `${TAG} Delete try`,
  props<{
    elementId: CanvasElementId;
    slotId?: ColumnId;
  }>(),
);

export const alertRemoveSuccess = createAction(
  `${TAG} Alert remove success`,
  props<{ alertId: string; elementId: string; path: string }>(),
);

/**
 * Used to remove element temporarily for dragging purposes.
 * It will be removed from its slot, but not from the element list.
 */
export const removeElementForDrag = createAction(
  `${TAG} Remove element for drag`,
  props<{ elementId: CanvasElementId }>(),
);

/** Update props of an existing element */
export const updateElementProps = createAction(
  `${TAG} Update element Props`,
  props<ElementPropUpdate>(),
);

export const revertElementPropsOverride = createAction(
  `${TAG} Revert element props override`,
  props<{
    elementId: CanvasElementId;
    slotId?: SlotId;
    target: CanvasElementOverrideTarget;
    setOnElement: boolean;
    setOnStyle: boolean;
    path: string;
  }>(),
);

export interface ResetElementStylePropsPayload {
  elementId: CanvasElementId;
  slotId?: SlotId;
  elementDefinition: CanvasElementDefinitionInternal;
}

export const resetElementStyleProps = createAction(
  `${TAG} Reset element Style Props`,
  props<ResetElementStylePropsPayload>(),
);

export interface TargetedPropsUpdateActionProps {
  /**
   * To which target props should be saved.
   * Desktop (default) -> element.props
   * Tablet -> element.overrides.tablet.props
   * Mobile -> element.overrides.mobile.props
   */
  target?: PropertiesEditorSavePropsTarget;
  /**
   * Should be saved only for the specified target.
   * If true, other targets will be cleared.
   */
  exclusive?: boolean;
}

export interface UpdateElementPropsBatchActionProps
  extends ElementPropsUpdate,
    TargetedPropsUpdateActionProps {}
export const updateElementPropsBatch = createAction(
  `${TAG} Batch update element Props`,
  props<UpdateElementPropsBatchActionProps>(),
);

export interface UpdateElementsPropsBatch extends TargetedPropsUpdateActionProps {
  updates: ElementPropsUpdate[];
  removeUndefinedFor?: ElementPropsSection[];
}
export const updateElementsPropsBatch = createAction(
  `${TAG} Batch update elements Props`,
  props<UpdateElementsPropsBatch>(),
);

export interface UpdateSlotPropsActionProps extends TargetedPropsUpdateActionProps {
  elementId: CanvasElementId;
  slotId: string;
  path: string;
  value: unknown;
}
/** Update props of an existing slot */
export const updateSlotProps = createAction(
  `${TAG} Update slot Props`,
  props<UpdateSlotPropsActionProps>(),
);

/** Reset a section of props of an existing element */
export const resetElementPropsSection = createAction(
  `${TAG} Reset element props section`,
  props<{ elementId: CanvasElementId; path: string }>(),
);

export const resetSlotPropsSection = createAction(
  `${TAG} Reset slot props section`,
  props<{ elementId: CanvasElementId; slotId: string; path: string }>(),
);

export const setElementProps = createAction(
  `${TAG} Set element props`,
  props<{
    elementId: CanvasElementId;
    props: PropsOf<any>;
  }>(),
);

export const setApiCallAsDataSource = createAction(
  `${TAG} Set API Call as Data Source`,
  props<{
    elementId: CanvasElementId;
    fields: FieldInfo[];
    inputWorkflow: Workflow;
    wrapperDefinition: CanvasElementDefinitionInternal;
    resultColumns: DatasetColumn[] | undefined;
  }>(),
);

export const updateElementWrapper = createAction(
  `${TAG} Update element wrapper`,
  props<{
    elementId: CanvasElementId;
    fields: FieldInfo[];
    inputWorkflow: Workflow;
    wrapperDefinition: CanvasElementDefinitionInternal;
  }>(),
);

export const updateElementWrapperSuccess = createAction(
  `${TAG} Update element wrapper success`,
  props<{
    elementId: CanvasElementId;
    workflow: Workflow;
  }>(),
);

export const updateElementWrapperFailure = createAction(
  `${TAG} Update element wrapper failure`,
  props<{
    elementId: CanvasElementId;
    workflowId: string;
  }>(),
);

export const appendFormAction = createAction(
  `${TAG} Append form action`,
  props<{
    formId: CanvasElementId;
    buttonId: CanvasElementId;
    workflowId: string;
  }>(),
);

/** Connects the layoutId/pageId (namespace) to element based on slot host type */
export const setNsForSlotHostType = createAction(
  `${TAG} Set namespace for slot host type`,
  props<{ hostType: PageType; ns: CanvasNamespaceId }>(),
);

export const addOverlayElement = createAction(
  `${TAG} Add overlay element`,
  props<{ overlayElement: OverlayElement }>(),
);

export const removeOverlayElement = createAction(
  `${TAG} Remove overlay element`,
  props<{ hostedNs: CanvasNamespaceId }>(),
);

export const updateDropdownDynamicSearchTry = createAction(
  `${TAG} Update dropdown dynamic search - try`,
  props<{
    elementId: CanvasElementId;
    columnKey: ColumnId | string;
    enable: boolean;
  }>(),
);

export const updateDropdownDynamicSearchWaitForWorkflow = createAction(
  `${TAG} Update dropdown dynamic search - wait for workflow`,
  props<{
    elementId: CanvasElementId;
    elementType: CanvasElementType;
    applyDynamicSearch: boolean | null;
  }>(),
);

export const updateDropdownDynamicSearch = createAction(
  `${TAG} Update dropdown dynamic search`,
  props<{
    elementId: CanvasElementId;
    columnKey: ColumnId | string;
    enable: boolean;
  }>(),
);

export const updateDropdownDynamicSearchSuccess = createAction(
  `${TAG} Update dropdown dynamic search - success`,
  props<{
    elementId: CanvasElementId;
    enable: boolean;
    userInputConnection: Connection | undefined;
    savedWorkflow: Workflow;
  }>(),
);

export const formRegenerated = createAction(
  `${TAG} Form regenerated`,
  props<{
    formId: CanvasElementId | undefined;
    commitButtonId: CanvasElementId | undefined;
    /**
     * The input source used to generate the form
     *
     * More details: {@link FormGenerationData.inputDataSource}
     */
    inputDataSource?: DataBindingSourceInfo;
    elementIdsToRemove: string[];
    elementsToUpsert: CanvasElement[];
  }>(),
);

/**
 * Asks for a questionnaire wizard to be regenerated: its slots, the one-field form behind each
 * question, and the fields inside them, all brought into line with the wizard's props.
 *
 * Declarative — the request names the wizard and nothing else, because the props already say
 * what the result should be. Whatever changed them (a question added, the data source swapped)
 * is the trigger's business, never the generation's.
 */
export const regenerateQuestionnaire = createAction(
  `${TAG} Regenerate questionnaire`,
  props<{ wizardId: CanvasElementId }>(),
);

/**
 * One questionnaire regeneration, committed as a diff over the document so it lands as a
 * single undo step and cannot revert editor writes it never saw (BL-9502).
 *
 * Both slices reduce it: the elements slice applies the element diff, the runtime slice the
 * connections that went with it. A workflow the regeneration left unwired needs nothing here —
 * the next layout save removes whatever lost its last connection.
 */
export const wizardQuestionnaireRegenerated = createAction(
  `${TAG} Wizard questionnaire regenerated`,
  props<{
    wizardId: CanvasElementId;
    elementIdsToRemove: CanvasElementId[];
    elementsToUpsert: CanvasElement[];
    /** Wiring of the wizard's step actions that no step claims anymore. */
    connectionIdsToRemove: ConnectionId[];
  }>(),
);

/**
 * Announces that an AI generation run has finished producing the given elements - not that any
 * particular latch should clear. Interpreters (e.g. the elements reducer) decide what "settled"
 * means for elements they own.
 */
export const aiGenerationSettled = createAction(
  `${TAG} AI generation settled`,
  props<{ elementIds: CanvasElementId[] }>(),
);

export const formColumnsRegenerateRequest = createAction(
  `${TAG} Form columns regenerate request`,
  props<{ elementId: CanvasElementId; value: number }>(),
);

export const formColumnsRegenerated = createAction(
  `${TAG} Form columns regenerated`,
  props<{
    entityState: EntityState<CanvasElement>;
    formId: CanvasElementId | undefined;
  }>(),
);

export const currentLayoutsColumnIdsAmended = createAction(
  `${TAG} Current layouts' column ids amended`,
  props<{ entityState: EntityState<CanvasElement> }>(),
);

export const formListRegenerated = createAction(
  `${TAG} Form List regenerated`,
  props<{
    entityState: EntityState<CanvasElement>;
    listId: CanvasElementId | undefined;
    commitButtonId: CanvasElementId | undefined;
    /**
     * The input source used to generate the form
     *
     * More details: {@link FormGenerationData.inputDataSource}
     */
    inputDataSource?: DataBindingSourceInfo;
    removedElementIds: string[];
  }>(),
);

/** Invoked after the selection gets finalized */
export const elementDataSourceSelected = createAction(
  `${TAG} Element's data source selected`,
  props<{
    elementId: CanvasElementId;
    elementType: CanvasElementType;
    selectedDataSource: DataBindingSourceInfo;
    dataSourceName?: string;
    isInitialSelection: boolean;
    /** null means dynamic search not supported by the source */
    applyDynamicSearch?: boolean | null;
  }>(),
);

export const elementDataSourceChangeTrigger = createAction(
  `${TAG} Element's data source change trigger`,
  props<{ workflowId: string; workflow: Workflow }>(),
);

export const refreshBinding = createAction(
  `${TAG} Refresh binding`,
  props<{
    elementId: CanvasElementId;
    bindingKey: CanvasElementInputBinding;
    isNewDataSource: boolean;
    visibleFields?: string[];
  }>(),
);

export const updateBinding = createAction(
  `${TAG} Update binding`,
  props<{
    bindingKey: CanvasElementInputBinding;
    elementId: CanvasElementId;
    /**
     * Stores the columns from the dataset on which the filters are applied (by Search & Filters mechanism)
     * Used for sanitization of filters.
     */
    filteredDatasetColumns?: DatasetColumn[];
    inputConfig?: CanvasElementInput;
    isNewDataSource: boolean;
    schema: BaseSchema | ElementSchema | WorkflowSchema;
    setNewColumnsVisible?: boolean;
    setNewColumnsEditable?: boolean;
    visibleFields?: string[];
  }>(),
);

export const sanitizeAndUpdateWrappedElement = createAction(
  `${TAG} Sanitize and update wrapped element`,
  props<{ elementId: CanvasElementId }>(),
);

export const validateElements = createAction(`${TAG} Validate elements`);

export const updateElementsActionPage = createAction(
  `${TAG} Update elements action page`,
  props<{ pageId: string; patch: { pageType: PageType } }>(),
);

export const clearPageRefs = createAction(
  `${TAG} Clear page references`,
  props<{ pageId: string }>(),
);

export const clearPageRefsFromElement = createAction(
  `${TAG} Clear page references from element`,
  props<{ elementId: CanvasElementId }>(),
);

export const tryConnectingWorkflow = createAction(
  `${TAG} Try connecting workflow`,
  props<{ workflow: Workflow }>(),
);

export const swapElementType = createAction(
  `${TAG} Swap element type`,
  props<{
    elementId: CanvasElementId;
    sourceType: CanvasElementType;
    targetType: CanvasElementType;
  }>(),
);

export const swapElementTypeSuccess = createAction(
  `${TAG} Swap element type success`,
  props<{ elementId: CanvasElementId; entity: CanvasElement }>(),
);

export const swapElementTypeFormOutputUpdate = createAction(
  `${TAG} Swap element type form output update`,
  props<{
    data: FormOutputUpdateData | FormOutputUpdateData[] | undefined;
    formOutputEventType: RuntimeEventType;
  }>(),
);

export const pageMainDatasetIdUpdated = createAction(
  `${TAG} Page main dataset id updated`,
  props<{
    page: PageInfo;
    mainDatasetId: string;
  }>(),
);

export const focusReferencingPageElement = createAction(
  `${TAG} Focus referencing page element`,
  props<{ layoutId: string }>(),
);

export const removeEmptyActionsFromElement = createAction(
  `${TAG} Remove empty actions from element`,
  props<{ elementId: string; pathToCheck: string }>(),
);

export const removeEmptyFiltersFromElement = createAction(
  `${TAG} Remove empty filters from element`,
  props<{ elementId: string }>(),
);

export const showEditFiltersDialog = createAction(
  `${TAG} Show edit filters dialog`,
  props<{
    elementId: CanvasElementId;
    elementType: CanvasElementType;
    bindingKey: CanvasElementInputBinding;
    dataBindings: CanvasElementDataProperties[];
    dataSource: DataBindingSourceInfo | undefined;
    limitResults: boolean;
    mode: DataSourceSelectMode | undefined;
  }>(),
);

export const updateFormGroupDataSource = createAction(
  `${TAG} Update Form Group data source`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    dataSource: DataBindingSourceInfo;
    styleSheets: StyleSheet[];
    formContainerDefinition: CanvasElementDefinitionInternal;
    buttonDefinition: CanvasElementDefinitionInternal;
  }>(),
);

export const refreshFormGroup = createAction(
  `${TAG} Refresh Form Group`,
  props<{
    elementId: CanvasElementId;
    updateDataSources?: boolean;
  }>(),
);

export const refreshFormGroupMainForm = createAction(
  `${TAG} Refresh Main Form of Form Group`,
  props<{
    elementId: CanvasElementId;
    action: FormActionType;
  }>(),
);

export const setElementSearchColumns = createAction(
  `${TAG} Set element search fields`,
  props<{
    elementId: CanvasElementId;
    useUserSelectedSearchColumns: boolean;
    columns?: (ColumnId | string)[];
  }>(),
);

export const setElementFilterBuilderColumns = createAction(
  `${TAG} Set element filter builder fields`,
  props<{
    elementId: CanvasElementId;
    useUserSelectedFilterBuilderColumns: boolean;
    columns?: string[];
  }>(),
);

export const addStepContainerSummaryForm = createAction(
  `${TAG} Add Step Container summary Form`,
  props<{
    elementId: CanvasElementId;
    slotId: ColumnId;
    formId: CanvasElementId;
  }>(),
);

export const addSlot = createAction(
  `${TAG} Add slot`,
  props<{
    elementId: CanvasElementId;
    index: number;
  }>(),
);

export const moveSlot = createAction(
  `${TAG} Move slot`,
  props<{
    elementId: CanvasElementId;
    oldIndex: number;
    newIndex: number;
  }>(),
);

export const deleteSlot = createAction(
  `${TAG} Delete slot`,
  props<{
    elementId: CanvasElementId;
    index: number;
  }>(),
);

export const requestCreateStyleSheet = createAction(
  `${TAG} Request create style sheet`,
  props<{
    name: string;
    props: any;
    overrides?: CanvasElementOverrides;
    elementId: string;
    slotId?: string;
    projectId: string;
    themeId: string | undefined;
    setDefault?: boolean;
  }>(),
);

export const requestRemoveStyleSheet = createAction(
  `${TAG} Request remove style sheet`,
  props<{
    elementId: CanvasElementId;
    styleProps: PartialPropsOf;
  }>(),
);

export const assignStyleSheet = createAction(
  `${TAG} Assign style sheet`,
  props<{
    elementId: CanvasElementId;
    slotId?: SlotId;
    styleSheetId: string;
  }>(),
);

export const setStyleSheet = createAction(
  `${TAG} Set style sheet`,
  props<{
    elementId: CanvasElementId;
    slotId?: SlotId;
    styleSheet: StyleSheet;
    elementDefinition: CanvasElementDefinitionInternal;
  }>(),
);

export const detachStyleSheet = createAction(
  `${TAG} Detach style sheet`,
  props<{
    elementId: CanvasElementId;
    slotId?: SlotId;
    styleProps: PartialPropsOf;
    styleOverrides?: CanvasElementOverrides;
  }>(),
);

export const migrateGlobalStyleToCurrentTheme = createAction(
  `${TAG} Migrate global style sheet to current theme`,
  props<{
    styleId: string;
    newStyleName?: string;
  }>(),
);

export const setStyleSheetForAction = createAction(
  `${TAG} Set style sheet for action`,
  props<{
    elementId: string;
    actionId: ItemContainerActionsIds;
    styleSheetId?: string | null;
    styleSheetName?: string | null;
  }>(),
);

export const copyInactiveStyleSheetProps = createAction(
  `${TAG} Copy inactive style sheet props`,
  props<{
    elementIds: CanvasElementId[];
    props: Record<string, any> | undefined;
  }>(),
);

export const navigateToNode = createAction(
  `${TAG} Navigate to element`,
  props<{ elementId: CanvasElementId; slotId?: SlotId }>(),
);

export const deferActions = createAction(
  `${TAG} Defer actions`,
  props<{
    actions: Action[];
  }>(),
);

export const elementPropsActionTrigger = createAction(
  `${TAG} Element props action trigger`,
  props<{ elementId: CanvasElementId; action: PropertiesConfigSubsectionTriggerKey; args: any }>(),
);

export const initializeAvatarFields = createAction(
  `${TAG} Initialize Avatar fields`,
  props<{ elementId: CanvasElementId; schema: BlazeTableSchema }>(),
);

export const swapElementPageType = createAction(
  `${TAG} Swap element page type`,
  props<{ elementId: CanvasElementId; value: string }>(),
);

export const setIsUsedToCreateNewPages = createAction(
  `${TAG} Set is used to create new pages`,
  props<{ elementId: CanvasElementId; pageType: NavigationPageType; value: boolean }>(),
);

export const setElementDescription = createAction(
  `${TAG} set element description`,
  props<{
    elementId: CanvasElementId;
    description: string;
  }>(),
);
